"use client";

import { IFaireIcon } from "@faire/web-api/indigofair/data/IFaireIcon";
import React, { ReactNode, useMemo } from "react";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useGenerateBrandUrlFn } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useBrandUrl";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useOnBrandClick } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useOnBrandClick";
import * as RetailerPaths from "@faire/retailer/paths";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ProductClickForSearchPageParameters } from "../../ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { MarketplaceProductBrandLink } from "../../MarketplaceProductBrandLink";

interface ISearchMarketplaceProductBrandLinkProps {
  brandToken: string | undefined;
  brandIcon?: IFaireIcon | undefined;
  trackingParams?: ProductClickForSearchPageParameters;
  onBrandLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  children: ReactNode;
}

export const SearchMarketplaceProductBrandLink: React.FC<
  ISearchMarketplaceProductBrandLinkProps
> = ({
  brandToken,
  children,
  trackingParams,
  onBrandLinkClick,
  generateBrandUrl,
}) => {
  const trackingParamsBrandClickHandler = useOnBrandClick(
    trackingParams ?? {}
  ).onBrandClick;
  const onBrandClick = onBrandLinkClick ?? trackingParamsBrandClickHandler;

  const trackingParamsGenerateBrandUrlFn = useGenerateBrandUrlFn({
    productToken: trackingParams?.token,
    brandToken: brandToken,
    impressionId: trackingParams?.impressionId,
  });
  const generateBrandUrlFn =
    generateBrandUrl ?? trackingParamsGenerateBrandUrlFn;

  const brandUrl = useMemo(() => {
    if (!brandToken) {
      return undefined;
    }
    if (!generateBrandUrlFn) {
      return RetailerPaths.brandPage(brandToken);
    }
    return generateBrandUrlFn(brandToken);
  }, [brandToken, generateBrandUrlFn]);

  return (
    <MarketplaceProductBrandLink
      token={brandToken}
      onClick={(ev) => {
        onBrandClick?.(ev);
      }}
      data-test-id="marketplace-product-brand-link"
      to={brandUrl}
    >
      {children}
    </MarketplaceProductBrandLink>
  );
};
